import { useCallback, useEffect, useState } from 'react'
import cx from 'classnames'
import Styles from './LinkedinPreview.module.scss'
import { MediaRenderer } from '../components/MediaRenderer/MediaRenderer'
import useEmblaCarousel from 'embla-carousel-react'
import {
  PreviewCarouselArrows,
  PREVIEW_CAROUSEL_HOVER_TARGET,
} from '../components/PreviewCarouselArrows/PreviewCarouselArrows'
import { MediaLayoutProps } from './ImageGridMedia'
import { useOptionalCurrentSlide } from 'screens/Calendar/FullScreenPreview/CurrentSlideContext'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useHover } from '@react-aria/interactions'

interface CarouselMediaProps extends MediaLayoutProps {
  carouselTitle?: string
}

export const CarouselMedia = ({ mediaObjects, carouselTitle, ...mediaProps }: CarouselMediaProps) => {
  const [imageIndex, setImageIndex] = useState(0)
  const currentSlideContext = useOptionalCurrentSlide()
  useEffect(() => {
    currentSlideContext?.setCurrentSlideIndex(imageIndex, ExternalIntegrationEnum.LinkedIn)
  }, [imageIndex, currentSlideContext])
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'start' })
  const [isCarouselHovered, setIsCarouselHovered] = useState(false)
  const { hoverProps: carouselHoverProps } = useHover({
    onHoverStart: () => setIsCarouselHovered(true),
    onHoverEnd: () => setIsCarouselHovered(false),
  })

  const onEmblaSelect = useCallback(() => {
    if (!emblaApi) return
    setImageIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.on('select', onEmblaSelect)
    emblaApi.on('reInit', onEmblaSelect)
    onEmblaSelect()
    return () => {
      emblaApi.off('select', onEmblaSelect)
      emblaApi.off('reInit', onEmblaSelect)
    }
  }, [emblaApi, onEmblaSelect])

  return (
    <div
      className={cx(Styles.carouselWrapper, PREVIEW_CAROUSEL_HOVER_TARGET)}
      data-testid="linkedin-carousel"
      {...carouselHoverProps}
    >
      <div className={Styles.carouselImageArea}>
        <div className={Styles.carouselHeader}>
          {carouselTitle && <strong>{carouselTitle}</strong>}
          {carouselTitle && ' · '}
          {mediaObjects.length} pages
        </div>
        <div className={Styles.carouselViewport} ref={emblaRef}>
          <div className={Styles.carouselContainer}>
            {mediaObjects.filter(Boolean).map((mediaObject) => {
              const mediaUrl = mediaObject?.thumbnail_url || mediaObject?.source_url
              if (!mediaUrl) return null
              return (
                <div key={mediaUrl} className={Styles.carouselSlide}>
                  <MediaRenderer
                    mediaObject={mediaObject}
                    alt=""
                    className={Styles.imagePreview}
                    showVideoControls={mediaProps.showVideoControls}
                    showHoverActionOverlay={mediaProps.showHoverActionOverlay}
                    almanacFileKey={mediaProps.almanacFileKey}
                    isActive={mediaProps.isActive}
                    isPressable={!mediaProps.showHoverActionOverlay}
                    isGenerating={mediaProps.isGenerating}
                    onClose={mediaProps.onClose}
                    allMediaObjects={mediaObjects}
                    editOverlay={{
                      position: 'top',
                      forceShow: isCarouselHovered,
                      padding: '48px 12px 12px',
                      gradient: true,
                    }}
                  />
                </div>
              )
            })}
          </div>
        </div>
        {!mediaProps.isGenerating && <PreviewCarouselArrows emblaApi={emblaApi} />}
        <div className={Styles.carouselFooter}>
          <span className={Styles.carouselCounter}>
            {imageIndex + 1} / {mediaObjects.length}
          </span>
          <div className={Styles.progressBar}>
            <div
              className={Styles.progressFill}
              style={{ width: `${(imageIndex / (mediaObjects.length - 1)) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
